import {
  useCreateNoteMutation,
  useUpdateNoteMutation,
} from '@carboncopyco/data';
import type { NoteEntityType } from '@carboncopyco/types';
import {
  Button,
  ErrorBox,
  Loading,
  SkeletonTextLoading,
  notify,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { SPEAKER_NOTES_MAX_LENGTH } from '../consts';
import { DiscardChangesModal } from './DiscardChangesModal';
import { EmptyState } from './EmptyState';
import { useSpeakerNotes } from './useSpeakerNotes';

export type SpeakerNotesProps = {
  isEditable?: boolean;
  entityType: NoteEntityType;
  entityId: string;
  value: string;
  setValue: (value: string) => void;
  onAddNote?: () => void;
};
export const SpeakerNotes = ({
  isEditable,
  entityType,
  entityId,
  value,
  setValue,
  onAddNote,
}: SpeakerNotesProps) => {
  const { organizationId } = useParams();
  const [showDiscardModal, setShowDiscardModal] = useBoolean();
  const {
    note: savedNote,
    isLoading,
    error,
  } = useSpeakerNotes({ entityType, entityId });
  const isEmpty = useMemo(
    () => !savedNote?.content?.length && !value,
    [savedNote, value]
  );
  const [active, setActive] = useBoolean();
  const hasUnsavedChanges = savedNote ? savedNote.content !== value : !!value;
  const [createNote, { isLoading: isCreatingNote }] = useCreateNoteMutation();
  const [updateNote, { isLoading: isUpdatingNote }] = useUpdateNoteMutation();
  const isSaving = isCreatingNote || isUpdatingNote;
  const actionButtonsDisabled = isSaving || !hasUnsavedChanges;

  useEffect(() => {
    if (savedNote?.content) {
      setValue(savedNote.content);
    }
  }, [savedNote, setValue]);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const actionButtonsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (active) {
      textAreaRef?.current?.focus();
    }
  }, [active]);

  const handleSave = () => {
    savedNote?.id
      ? updateNote({
          noteId: savedNote?.id,
          organizationId,
          content: value,
        })
          .unwrap()
          .then(() => {
            notify.success('Speaker notes have been updated successfully.');
            setActive.setFalse();
          })
          .catch(() => {
            notify.error('Error updating speaker notes');
          })
      : createNote({
          organizationId,
          entityType,
          entityId,
          content: value,
        })
          .unwrap()
          .then(() => {
            notify.success('Speaker notes have been created successfully.');
            setActive.setFalse();
          })
          .catch(() => {
            notify.error('Error creating speaker notes');
          });
  };
  if (error) {
    return (
      <ErrorBox
        contained
        title="Error fetching notes"
      />
    );
  }

  if (isLoading) {
    return <SkeletonTextLoading rows={4} />;
  }

  return (
    <>
      {active || !isEmpty ? (
        <div className="h-full flex flex-col gap-4">
          <div
            className={classNames(
              'grow border rounded-md border-transparent',
              isSaving
                ? 'pointer-events-none opacity-50'
                : 'focus-within focus-within:border-ds-primary-300 hover:focus-within hover:focus-within:border-ds-primary-300',
              { 'hover:border-ds-natural-300': isEditable }
            )}
          >
            <textarea
              autoFocus
              onClick={setActive.setTrue}
              maxLength={SPEAKER_NOTES_MAX_LENGTH}
              ref={textAreaRef}
              disabled={!isEditable}
              value={value}
              className={classNames(
                'resize-none h-full w-full rounded-md',
                'ring-0 border-none focus:ring-0 focus:border-none'
              )}
              onChange={(event) => {
                setValue(event.target.value);
              }}
            />
          </div>
          {isEditable && (
            <div
              ref={actionButtonsRef}
              className="flex items-center justify-end gap-2 self-end"
            >
              <Button
                secondary
                link
                label="Cancel"
                onClick={setShowDiscardModal.setTrue}
                disabled={actionButtonsDisabled}
              />
              <Button
                onClick={handleSave}
                disabled={actionButtonsDisabled}
              >
                <div className="w-ds-18 flex items-center justify-center">
                  {isSaving ? <Loading color="text-ds-primary-500" /> : 'Save'}
                </div>
              </Button>
            </div>
          )}
        </div>
      ) : (
        <EmptyState
          isEditable={isEditable}
          onAddNote={() => {
            onAddNote?.();
            setActive.setTrue();
          }}
        />
      )}
      {showDiscardModal && (
        <DiscardChangesModal
          onDiscard={() => {
            setValue(savedNote?.content || '');
            setShowDiscardModal.setFalse();
          }}
          onCancel={() => {
            setShowDiscardModal.setFalse();
            textAreaRef.current.focus();
          }}
        />
      )}
    </>
  );
};
